import { useState } from 'react';
import type { Dispatch } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import FlipIcon from '@mui/icons-material/Flip';
import { DocumentCaptureZone } from '../../components/DocumentCaptureZone';
import { DOC_TYPE } from '../../types/kyc';
import type { KYCFlowState, KYCAction } from '../KYCFlowController';
import { uploadDocument, mergeIneFiles, executeWorkflow, KYCApiError } from '../../services/kycApi';

interface CaptureINEBackStepProps {
  state: KYCFlowState;
  dispatch: Dispatch<KYCAction>;
}

// Paso de captura del reverso de la INE.
// Se muestra cuando el backend detectó que el archivo subido en CaptureIDStep
// es una INE con solo una página (anverso), y solicita al usuario que suba el reverso.
// Al confirmar:
//   1. Sube el reverso al doc-handler.
//   2. Llama a /merge-ine para fusionar anverso + reverso en un PDF de 2 páginas.
//   3. Dispara el workflow de clasificación sobre el PDF fusionado.
//   4. Transiciona a VALIDATING_ID — el SSE emitirá score_complete con el resultado final.
export function CaptureINEBackStep({ state, dispatch }: CaptureINEBackStepProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async () => {
    const fileBack = selectedFile;
    if (!fileBack) return;

    // ineFilenameFront garantizado no-null en este estado (se guardó en SET_INE_FRONT_DATA)
    const filenameFront = state.ineFilenameFront!;

    setProcessing(true);
    dispatch({ type: 'SET_ERROR', message: '' });

    try {
      // Paso 1: subir el reverso al doc-handler (solo para tener la referencia)
      await uploadDocument({
        file: fileBack,
        sessionId: state.session.sessionId,
        token: state.session.token,
      });

      // Paso 2: fusionar anverso + reverso en un único PDF de 2 páginas
      const merged = await mergeIneFiles({
        fileBack,
        filenameFront,
        sessionId: state.session.sessionId,
        token: state.session.token,
      });

      // Paso 3: actualizar el filename definitivo con el PDF fusionado (anverso + reverso).
      // Sobreescribe el filename del anverso guardado en CaptureIDStep — el PDF fusionado
      // es el documento completo que se usará en la comparación facial de liveness.
      dispatch({
        type: 'SET_FINAL_DOC_FILENAME',
        filename: merged.filename,
      });

      // Paso 4: transicionar a VALIDATING_ID antes de disparar el workflow
      dispatch({ type: 'SET_ID_DOC_TYPE', docType: DOC_TYPE.INE });

      // Paso 4: disparar el pipeline de clasificación sobre el PDF fusionado
      await executeWorkflow({
        action: 'classification',
        sessionId: state.session.sessionId,
        token: state.session.token,
        payload: {
          process_id: merged.processId,
          filename: merged.filename,
          content_type: 'application/pdf',
        },
      });

      // A partir de aquí el KYCFlowController recibe el resultado vía SSE (score_complete)
    } catch (err) {
      // Permanecer en CAPTURING_INE_BACK para que el usuario pueda reintentar
      const message =
        err instanceof KYCApiError
          ? err.message
          : 'Ocurrió un error al procesar el reverso de tu INE. Intenta de nuevo.';
      dispatch({ type: 'SET_ERROR', message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FlipIcon color="primary" />
        <Typography variant="h6">Reverso de tu INE</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        El sistema detectó que subiste solo el anverso de tu INE. Por favor sube también el
        reverso para continuar con la verificación.
      </Typography>

      {/* Alerta de error */}
      {state.errorMessage && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {state.errorMessage}
        </Alert>
      )}

      {/* Zona de captura del reverso */}
      <DocumentCaptureZone
        hasFile={selectedFile !== null}
        onFileSelected={setSelectedFile}
        dropHint="Arrastra el reverso de tu INE aquí, o haz clic para seleccionar"
        cameraLabel="Coloca el reverso de tu INE dentro del marco"
        docVariant="ine_back"
      />

      {/* Botón de envío */}
      <Button
        variant="contained"
        size="large"
        disabled={selectedFile === null || processing}
        onClick={handleSubmit}
        fullWidth
        startIcon={processing ? <CircularProgress size={20} color="inherit" /> : undefined}
      >
        {processing ? 'Procesando...' : 'Continuar verificación'}
      </Button>
    </Box>
  );
}
